/**
 * PerformanceSection — real-user Core Web Vitals overview for the
 * active site. Composed of three stacked pieces:
 *
 *   1. Filter row — audience (All devices · Phone · Tablet · Desktop),
 *      country, connection and date range as `FilterSelect` dropdown
 *      pills, plus the route's Compare pill in the trailing `actions`
 *      slot. They scope the tiles and the chart together so the user
 *      only picks the audience once per page visit.
 *   2. Five `MetricTile`s — LCP · INP · CLS · TTFB · FCP. Hero value
 *      is the period p75 from `pixel_site_perf`; bars below are the
 *      daily p75 series from `pixel_site_perf_trend` (weekly-bucketed
 *      on long, dense ranges). The good-density % + sample count live
 *      in the tile's hover tooltip rather than always-on caption text;
 *      the caption only speaks to report an empty window. Hover-explore
 *      lives on the tile header and links to /metrics/explore.
 *   3. Metric-selector trend chart — one metric (pill picker), one
 *      band-coloured line: averaged across devices for "All", or the
 *      selected device, drawn with monotone-cubic interpolation (no
 *      overshoot) and a single area fill. Weekly-bucketed on long
 *      dense ranges.
 *
 * Data path:
 *   - `useSitePixelPerf` → period p75 + good-density + sample count.
 *   - `useSitePixelPerfTrend` → daily series. Both pipes already
 *     deployed (`packages/tinybird/tinybird/pipes/`).
 *
 * Three-state rendering — matches the Traffic surface's `RumKpiRow`
 * pattern so the two pages behave the same way during cold load:
 *
 *   - `loading` : queries in flight, no cache → render plain
 *                 bordered tile frames + chart-shaped frame. No
 *                 inner shapes (avoids "fake data appeared then
 *                 disappeared" if the query resolves to empty).
 *   - `empty`   : queries resolved, zero samples → render the full
 *                 tile chrome with the label, a "—" hero, and a
 *                 faint placeholder bar shape. Chart slot fills
 *                 with a calm inline "No {metric} samples in this
 *                 window." (not a curve — that read as still-loading).
 *                 The route-level `RumPlaceholderBanner` instructs the
 *                 user on installation; tiles stay quiet about it to
 *                 avoid re-saying the same thing five times.
 *   - `data`    : real values + real bars + caption with %good +
 *                 sample count. The whole grid is keyed by state
 *                 so React remounts once on the loading→data /
 *                 empty→data transition, letting `animate-in
 *                 fade-in duration-300` soften the swap.
 */
import {
  AUDIT_SCOPE_ORIGIN,
  AuditFilterBar,
  AuditMetricCharts,
  AuditScopeSelect,
  type DeviceFilter,
  PageSelect,
  regionLabel,
} from '@/components/audits/AuditMetricCharts';
import {
  COMPARE_SERIES_COLOR,
  TrendCurve,
  type TrendCurveSpec,
  type TrendLine,
} from '@/components/charts/TrendCurve';
import type { CondensedBarsDatum } from '@/components/primitives/CondensedBars';
import { FilterBar } from '@/components/primitives/FilterBar';
import { FilterSelect } from '@/components/primitives/FilterSelect';
import { Flag } from '@/components/primitives/Flag';
import { MetricTile } from '@/components/primitives/MetricTile';
import { type PageRange, PageRangeSelect } from '@/components/primitives/PageRangePill';
import { PlaceholderBars } from '@/components/primitives/PlaceholderBars';
import { CruxSection } from '@/components/rum/CruxSection';
import { parseClickHouseUtc } from '@/components/rum/PageTrafficView';
import { Tooltip, TooltipPopup, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { PageWithRecentAudits, TrendBucketRow } from '@/lib/api-types';
import {
  monotoneSegment,
  monotoneTangents,
  shouldBucketSparkbarsWeekly,
  smoothPath,
} from '@/lib/curve';
import {
  type MetricBand,
  bandFill,
  bandText,
  clsBand,
  fcpBand,
  formatMs,
  inpBand,
  lcpBand,
  scoreBand,
  ttfbBand,
} from '@/lib/format';
import { useAudits } from '@/lib/queries/audits';
import type { CruxResponse } from '@/lib/queries/crux';
import { type DeployMarker, useSiteDeploys } from '@/lib/queries/deploys';
import {
  type PixelPerfByCountryRow,
  type PixelPerfByPageRow,
  type PixelPerfRow,
  type PixelPerfTrendRow,
  type PixelWorstSessionRow,
  useSiteWorstSessions,
} from '@/lib/queries/pixel';
import { useSite, useSitePagesWithAudits, useSiteTrend } from '@/lib/queries/sites';
import { cn } from '@/lib/utils';
import type { Icon } from '@phosphor-icons/react';
import {
  ArrowRight,
  CalendarCheck,
  CellSignalFull,
  ChartBar,
  ChartLine,
  Desktop,
  DeviceMobile,
  DeviceTablet,
  File,
  Globe,
  GlobeHemisphereWest,
  GoogleChromeLogo,
  Info,
  Pulse,
  TrendUp,
} from '@phosphor-icons/react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Fragment, type ReactElement, type ReactNode, useMemo, useState } from 'react';

// ─── Public types ───────────────────────────────────────────────────

export type PerfFormFactor = 'all' | 'phone' | 'tablet' | 'desktop';

/**
 * Friendly bucket over the raw Network Information API `network_type`
 * values. Maps:
 *
 *   - `all`     → no `network_types` param sent (= every visitor)
 *   - `fast`    → ['4g']
 *   - `slower`  → ['3g', '2g', 'slow-2g']
 *   - `unknown` → ['unknown']
 *
 * Keeping the friendly bucket in the component contract (rather than
 * exposing raw connection strings to the route) means the labels are
 * authoritative in one place and changes — e.g. when 5G shows up
 * widely — only touch the mapping.
 */
export type PerfConnection = 'all' | 'fast' | 'slower' | 'unknown';

/** Concrete network_type strings that compose each friendly bucket. */
export const NETWORK_TYPES_BY_CONNECTION: Record<Exclude<PerfConnection, 'all'>, string[]> = {
  fast: ['4g'],
  slower: ['3g', '2g', 'slow-2g'],
  unknown: ['unknown'],
};

interface PerformanceSectionProps {
  perf: PixelPerfRow[] | undefined;
  trend: PixelPerfTrendRow[] | undefined;
  /** Same series over 2× the window — drives the trend chart's dashed
   *  previous-period comparison line. Undefined while loading. */
  trendCompare?: PixelPerfTrendRow[] | undefined;
  /** One row per country with CWV p75 + samples — drives the Country
   *  Ranking card below the trend chart. Undefined while loading. */
  perfByCountry?: PixelPerfByCountryRow[] | undefined;
  /** One row per page with CWV p75 + samples + pageviews — drives the
   *  Page ranking + Page popularity cards. Undefined while loading. */
  perfByPage?: PixelPerfByPageRow[] | undefined;
  /** Initial-load gate. True only while the very first fetch is in
   *  flight and the cache is empty — refetches keyed on form-factor
   *  use `keepPreviousData` so they don't flip this back to true and
   *  cause the data → loading-shell flicker. */
  loading: boolean;
  /** Page-level date range — drives the perf + perf-trend queries
   *  upstream. Threaded through here so the range pill sits in the
   *  same filter row as Audience / Country / Connection. */
  range: PageRange;
  onRangeChange: (next: PageRange) => void;
  /** Currently-selected form-factor scope. Controls both the tiles
   *  and the trend chart so the user picks once. */
  formFactor: PerfFormFactor;
  onFormFactorChange: (next: PerfFormFactor) => void;
  /** Country filter — ISO 3166-1 alpha-2 code or 'all'. The route
   *  owns the state because the country list is derived from a
   *  separate breakdown query also owned at the route level. */
  country: string;
  onCountryChange: (next: string) => void;
  /** Country codes that actually have data for this site over the
   *  active window. Drives the dropdown options — only countries
   *  with samples are surfaced, sorted by traffic at the route. */
  countryOptions: string[];
  /** Connection bucket — friendly label, not raw network_type. */
  connection: PerfConnection;
  onConnectionChange: (next: PerfConnection) => void;
  /** Selected vital — drives the trend chart AND the three ranking
   *  cards. Owned by the route (URL-backed `?metric=`) so the view is
   *  shareable and the Overview's tile chips can deep-link straight
   *  into a metric, the same way `?view=` works for the tabs. */
  metric: MetricKey;
  onMetricChange: (next: MetricKey) => void;
  /** Hover-explore wiring on each tile. */
  siteUuid: string;
  /** Guest-site comparison (page-level `?compare=`). When set, the CWV
   *  tiles caption the guest's p75 and the trend chart overlays the
   *  guest's line. All three undefined when comparison is off. */
  comparePerf?: PixelPerfRow[] | undefined;
  compareTrend?: PixelPerfTrendRow[] | undefined;
  compareName?: string | undefined;
  /** Active site's display name — labels the home line when comparing. */
  homeName?: string | undefined;
  /** Trailing slot in the filter row — the route drops its Compare
   *  picker here so the page's filters and its compare control read as
   *  one strip. Route-owned because `?compare=` is route state and the
   *  embed slice doesn't offer it. */
  actions?: ReactNode;
}

/** Three-state model — see the file docstring. Computed once at the
 *  section level so the tile row and the trend chart agree. */
type SectionState = 'loading' | 'empty' | 'data';

function deriveState(perf: PixelPerfRow[] | undefined, loading: boolean): SectionState {
  if (loading) return 'loading';
  if (!perf || perf.length === 0) return 'empty';
  const anySamples = perf.some(
    (r) =>
      r.lcp_sample_count +
        r.inp_sample_count +
        r.cls_sample_count +
        r.ttfb_sample_count +
        r.fcp_sample_count >
      0,
  );
  return anySamples ? 'data' : 'empty';
}

// ─── Metric registry ────────────────────────────────────────────────
//
// Single source of truth for which metrics this section surfaces, what
// they're called, how they're formatted, and which `*Band` lives next
// to each value. Adding a metric is one new entry here + a `pickValue`
// branch — the tile row + trend chart pill picker both derive from it.

/** The five real-user vitals. Also the value space of the route's
 *  `?metric=` search param — shared by the Real users + Field data
 *  tabs, which surface the same five — so the route can validate an
 *  incoming value against this list. */
export const CWV_METRIC_KEYS = ['lcp', 'inp', 'cls', 'ttfb', 'fcp'] as const;

export type MetricKey = (typeof CWV_METRIC_KEYS)[number];

interface MetricSpec {
  key: MetricKey;
  label: string;
  unit: 'ms' | 'cls';
  band: (v: number | null | undefined) => MetricBand;
  format: (v: number | null | undefined) => string;
  /** Upper bound of the "good" band — the value where the curve
   *  starts tinting amber. Drives the dashed reference line, the
   *  threshold pill label, and the y-axis lower bound. */
  goodThreshold: number;
  /** Upper bound of "needs-improvement" / start of "poor". Pads the
   *  y-axis upper bound so the threshold line + amber segments never
   *  clip off the top. */
  poorThreshold: number;
}

const formatCls = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : v.toFixed(2);

const METRICS: readonly MetricSpec[] = [
  // Thresholds mirror the band functions in lib/format.ts. Keep them
  // in sync — they're duplicated here because the chart needs the raw
  // numbers (for the dashed line + pill), not just the band classifier.
  {
    key: 'lcp',
    label: 'LCP',
    unit: 'ms',
    band: lcpBand,
    format: formatMs,
    goodThreshold: 2500,
    poorThreshold: 4000,
  },
  {
    key: 'inp',
    label: 'INP',
    unit: 'ms',
    band: inpBand,
    format: formatMs,
    goodThreshold: 200,
    poorThreshold: 500,
  },
  {
    key: 'cls',
    label: 'CLS',
    unit: 'cls',
    band: clsBand,
    format: formatCls,
    goodThreshold: 0.1,
    poorThreshold: 0.25,
  },
  {
    key: 'ttfb',
    label: 'TTFB',
    unit: 'ms',
    band: ttfbBand,
    format: formatMs,
    goodThreshold: 800,
    poorThreshold: 1800,
  },
  {
    key: 'fcp',
    label: 'FCP',
    unit: 'ms',
    band: fcpBand,
    format: formatMs,
    goodThreshold: 1800,
    poorThreshold: 3000,
  },
] as const;

// ─── Lab (synthetic Lighthouse) score metrics ───────────────────────
//
// Higher-is-better, 0–100, with Lighthouse's standard 90 (good) / 50
// (poor) thresholds. Plotted from the synthetic `metrics/trend` snapshot
// data, NOT the RUM pixel trend — so the chart fetches a second source
// when one of these is selected. They live apart from `METRICS` so the
// tile row + country ranking (RUM-only) are untouched.

type LabScoreKey = 'performance' | 'accessibility' | 'best_practices' | 'seo';

interface LabMetricSpec {
  key: LabScoreKey;
  label: string;
  unit: 'score';
  band: (v: number | null | undefined) => MetricBand;
  format: (v: number | null | undefined) => string;
  goodThreshold: number;
  poorThreshold: number;
}

const formatScore = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : String(Math.round(v));

const LAB_METRICS: readonly LabMetricSpec[] = [
  {
    key: 'performance',
    label: 'Performance',
    unit: 'score',
    band: scoreBand,
    format: formatScore,
    goodThreshold: 90,
    poorThreshold: 50,
  },
  {
    key: 'accessibility',
    label: 'Accessibility',
    unit: 'score',
    band: scoreBand,
    format: formatScore,
    goodThreshold: 90,
    poorThreshold: 50,
  },
  {
    key: 'best_practices',
    label: 'Best practices',
    unit: 'score',
    band: scoreBand,
    format: formatScore,
    goodThreshold: 90,
    poorThreshold: 50,
  },
  {
    key: 'seo',
    label: 'SEO',
    unit: 'score',
    band: scoreBand,
    format: formatScore,
    goodThreshold: 90,
    poorThreshold: 50,
  },
] as const;

/** Which `TrendBucketRow` field backs each Lab score. */
const LAB_FIELD: Record<
  LabScoreKey,
  'avg_performance_score' | 'avg_accessibility_score' | 'avg_best_practices_score' | 'avg_seo_score'
> = {
  performance: 'avg_performance_score',
  accessibility: 'avg_accessibility_score',
  best_practices: 'avg_best_practices_score',
  seo: 'avg_seo_score',
};

/** The Core Web Vitals the synthetic origin trend carries, paired with
 *  their p75 column on `TrendBucketRow`. Reuses the RUM `METRICS`
 *  registry (same bands + formatters) so the audit summary tiles read
 *  identically to the Real users + Field data tiles — the Overview's
 *  whole point is comparing the same metric across planes. `p75_fcp` is
 *  optional on the row (added after the others), so the FCP tile shows
 *  an empty shell until the trend pipe redeploys and backfills it. */
const LAB_CWV_METRICS: ReadonlyArray<{
  spec: MetricSpec;
  field: 'p75_lcp' | 'p75_inp' | 'p75_cls' | 'p75_ttfb' | 'p75_fcp';
}> = [
  { spec: METRICS.find((m) => m.key === 'lcp')!, field: 'p75_lcp' },
  { spec: METRICS.find((m) => m.key === 'inp')!, field: 'p75_inp' },
  { spec: METRICS.find((m) => m.key === 'cls')!, field: 'p75_cls' },
  { spec: METRICS.find((m) => m.key === 'ttfb')!, field: 'p75_ttfb' },
  { spec: METRICS.find((m) => m.key === 'fcp')!, field: 'p75_fcp' },
];

export type ChartMetricKey = MetricKey | LabScoreKey;
type ChartMetricSpec = MetricSpec | LabMetricSpec;
const CHART_METRICS: readonly ChartMetricSpec[] = [...METRICS, ...LAB_METRICS];

/** Everything the Audits tab's chart can plot: the four Lighthouse
 *  category scores AND the five vitals the same synthetic trend rows
 *  carry (`p75_*`). Scores first, matching the order the two chip groups
 *  appear in above the chart. Giving Audits a vitals selector is what
 *  lets the Overview's audit tiles deep-link the way the other two
 *  planes' tiles do — before, the tab could only be filtered by score,
 *  so a per-vital click had nothing to select. */
const AUDIT_CHART_METRICS: readonly ChartMetricSpec[] = [...LAB_METRICS, ...METRICS];

/** Runtime value space of `?metric=`, for the route's validation. */
export const CHART_METRIC_KEYS: readonly ChartMetricKey[] = CHART_METRICS.map((m) => m.key);

function isLabSpec(spec: ChartMetricSpec): spec is LabMetricSpec {
  return spec.unit === 'score';
}

/** Pull a metric's p75 + density + sample count off the row shape. */
function pickRow(row: PixelPerfRow | PixelPerfTrendRow, metric: MetricKey) {
  switch (metric) {
    case 'lcp':
      return { p75: row.lcp_p75, good: row.lcp_good_density, samples: row.lcp_sample_count };
    case 'inp':
      return { p75: row.inp_p75, good: row.inp_good_density, samples: row.inp_sample_count };
    case 'cls':
      return { p75: row.cls_p75, good: row.cls_good_density, samples: row.cls_sample_count };
    case 'ttfb':
      return { p75: row.ttfb_p75, good: row.ttfb_good_density, samples: row.ttfb_sample_count };
    case 'fcp':
      return { p75: row.fcp_p75, good: row.fcp_good_density, samples: row.fcp_sample_count };
  }
}

// ─── Aggregation across form factors ────────────────────────────────
//
// `pixel_site_perf` returns one row per form factor. For the "All"
// audience we collapse rows by weighting each metric's p75 by its own
// `*_sample_count` — gives a defensible single number when phone +
// desktop have different population sizes. The good-density and
// sample sums are straightforward additions.
//
// Note: weighted-mean-of-p75 isn't a true p75 across the merged
// population — that would need the underlying t-digest states. We
// expose the underlying form-factor split in the trend chart's
// per-line view so power users can see the unblended picture.

interface AggregatedMetric {
  p75: number | null;
  good: number | null;
  samples: number;
}

function aggregateAcrossFormFactors(
  rows: PixelPerfRow[] | undefined,
  metric: MetricKey,
): AggregatedMetric {
  if (!rows || rows.length === 0) return { p75: null, good: null, samples: 0 };
  let weightedSum = 0;
  let totalWeight = 0;
  let goodWeighted = 0;
  let totalSamples = 0;
  for (const r of rows) {
    const m = pickRow(r, metric);
    totalSamples += m.samples;
    if (m.p75 != null && m.samples > 0) {
      weightedSum += m.p75 * m.samples;
      totalWeight += m.samples;
    }
    if (m.good != null && m.samples > 0) {
      goodWeighted += m.good * m.samples;
    }
  }
  return {
    p75: totalWeight > 0 ? weightedSum / totalWeight : null,
    good: totalSamples > 0 ? goodWeighted / totalSamples : null,
    samples: totalSamples,
  };
}

function pickFormFactor(
  rows: PixelPerfRow[] | undefined,
  formFactor: Exclude<PerfFormFactor, 'all'>,
  metric: MetricKey,
): AggregatedMetric {
  const row = rows?.find((r) => r.form_factor === formFactor);
  if (!row) return { p75: null, good: null, samples: 0 };
  const m = pickRow(row, metric);
  return { p75: m.p75, good: m.good, samples: m.samples };
}

function resolveMetric(
  rows: PixelPerfRow[] | undefined,
  formFactor: PerfFormFactor,
  metric: MetricKey,
): AggregatedMetric {
  const r =
    formFactor === 'all'
      ? aggregateAcrossFormFactors(rows, metric)
      : pickFormFactor(rows, formFactor, metric);
  // Authoritative "no data" signal is the sample count. A pre-aggregated
  // pipe row can carry a stale p75 with a zero sample_count — without
  // this guard the tile showed a value AND "No samples in window" at the
  // same time. Trust the count: no samples → no value.
  return r.samples > 0 ? r : { p75: null, good: null, samples: 0 };
}

// ─── Weekly bucketing (long ranges) ─────────────────────────────────
//
// On a 6- or 12-month window the per-day trend is hundreds of noisy
// points/bars. Past ~3 months we collapse to weekly averages so the
// curve + sparkbars read as a trend, not static.

/** True when the series is long AND dense enough to warrant weekly
 *  buckets. The density gate matters: a sparse 6-month window (only a
 *  handful of days with samples) must stay daily, or bucketing collapses
 *  it to one or two lonely points. */
function shouldBucketWeekly(rangeDays: number, pointCount: number): boolean {
  return rangeDays > 92 && pointCount > 84;
}

/** Group a sorted day→value series into ISO-week buckets (7-day windows
 *  from a fixed epoch, so gaps don't shift alignment), averaging the
 *  non-null values. The earliest day in each bucket labels it. */
function bucketWeekly(points: { day: string; value: number | null }[]): {
  day: string;
  value: number | null;
}[] {
  const WEEK_MS = 7 * 86_400_000;
  const groups = new Map<number, { day: string; vals: number[] }>();
  for (const p of points) {
    const t = Date.parse(`${p.day}T00:00:00Z`);
    if (Number.isNaN(t)) continue;
    const wk = Math.floor(t / WEEK_MS);
    const g = groups.get(wk) ?? { day: p.day, vals: [] };
    if (p.value != null) g.vals.push(p.value);
    if (p.day < g.day) g.day = p.day;
    groups.set(wk, g);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, g]) => ({
      day: g.day,
      value: g.vals.length ? g.vals.reduce((a, b) => a + b, 0) / g.vals.length : null,
    }));
}

// ─── Trend → bars ───────────────────────────────────────────────────
//
// Convert the daily perf-trend payload into `CondensedBars` data for
// one metric, scoped to the active form-factor filter. Days are kept
// chronological so the bars read oldest→newest.

interface BarMeta {
  day: string;
  formFactor: string;
}

function trendBars(
  trend: PixelPerfTrendRow[] | undefined,
  formFactor: PerfFormFactor,
  metric: MetricKey,
  rangeDays: number,
): CondensedBarsDatum<BarMeta>[] {
  if (!trend || trend.length === 0) return [];
  const filtered =
    formFactor === 'all'
      ? aggregateTrendByDay(trend)
      : trend.filter((r) => r.form_factor === formFactor);
  const ffLabel = formFactor === 'all' ? 'all' : formFactor;
  let points = filtered
    .slice()
    .sort((a, b) => a.day.localeCompare(b.day))
    .map((r) => ({ day: r.day, value: pickRow(r, metric).p75 }));
  // Tiny sparkline (~28 bars), so it buckets weekly sooner than the
  // full-width trend line below — at 90d, ~90 daily bars turn to mush.
  // Shared rule with the Traffic KPI bars so they're consistent.
  if (shouldBucketSparkbarsWeekly(rangeDays, points.length)) points = bucketWeekly(points);
  return points.map((p) => ({
    x: Date.parse(`${p.day}T00:00:00Z`),
    y: p.value,
    meta: { day: p.day, formFactor: ffLabel } satisfies BarMeta,
  }));
}

/** Weighted daily merge for the "All" form-factor case — same logic
 *  as `aggregateAcrossFormFactors` but per day. Output rows look like
 *  a trend row, with `form_factor='all'` so downstream code stays
 *  uniform. */
function aggregateTrendByDay(rows: PixelPerfTrendRow[]): PixelPerfTrendRow[] {
  const byDay = new Map<string, PixelPerfTrendRow[]>();
  for (const r of rows) {
    const list = byDay.get(r.day) ?? [];
    list.push(r);
    byDay.set(r.day, list);
  }
  const out: PixelPerfTrendRow[] = [];
  for (const [day, dayRows] of byDay) {
    out.push({
      day,
      form_factor: 'all',
      ...mergeRowMetrics(dayRows),
    });
  }
  return out;
}

function mergeRowMetrics(
  dayRows: PixelPerfTrendRow[],
): Omit<PixelPerfTrendRow, 'day' | 'form_factor'> {
  const blend = (metric: MetricKey): AggregatedMetric =>
    aggregateAcrossFormFactors(dayRows as unknown as PixelPerfRow[], metric);
  const lcp = blend('lcp');
  const inp = blend('inp');
  const cls = blend('cls');
  const ttfb = blend('ttfb');
  const fcp = blend('fcp');
  // `loaf_p75` isn't surfaced in the section but the shape demands it.
  // We don't have a useful blend for it without sample counts on the
  // pipe response, so pass through whichever row has it (rare in
  // practice — LoAF coverage is patchy).
  const loaf = dayRows.find((r) => r.loaf_p75 != null)?.loaf_p75 ?? null;
  return {
    lcp_p75: lcp.p75,
    lcp_good_density: lcp.good,
    lcp_sample_count: lcp.samples,
    inp_p75: inp.p75,
    inp_good_density: inp.good,
    inp_sample_count: inp.samples,
    cls_p75: cls.p75,
    cls_good_density: cls.good,
    cls_sample_count: cls.samples,
    ttfb_p75: ttfb.p75,
    ttfb_good_density: ttfb.good,
    ttfb_sample_count: ttfb.samples,
    fcp_p75: fcp.p75,
    fcp_good_density: fcp.good,
    fcp_sample_count: fcp.samples,
    loaf_p75: loaf,
  };
}

// ─── Form-factor segmented control ──────────────────────────────────
//
// Drives both the tile row and the trend chart. Icon set mirrors the
// per-page filter strip (Phone, Tablet, Desktop) so the affordance
// reads as the same control across surfaces.

const FORM_FACTOR_OPTIONS: ReadonlyArray<{
  value: PerfFormFactor;
  label: string;
  icon: Icon;
}> = [
  // Labels stand alone as the dropdown's trigger text — hence "All
  // devices" rather than the bare "All" a labelled segmented row could
  // get away with.
  { value: 'all', label: 'All devices', icon: GlobeHemisphereWest },
  { value: 'phone', label: 'Phone', icon: DeviceMobile },
  { value: 'tablet', label: 'Tablet', icon: DeviceTablet },
  { value: 'desktop', label: 'Desktop', icon: Desktop },
];

function FormFactorPicker({
  value,
  onChange,
}: {
  value: PerfFormFactor;
  onChange: (next: PerfFormFactor) => void;
}): ReactElement {
  const selected = FORM_FACTOR_OPTIONS.find((o) => o.value === value) ?? FORM_FACTOR_OPTIONS[0]!;
  const SelectedIcon = selected.icon;
  return (
    <FilterSelect
      ariaLabel="Audience"
      icon={<SelectedIcon aria-hidden="true" size={12} className="text-[var(--color-mute)]" />}
      value={value}
      options={FORM_FACTOR_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        return {
          value: opt.value,
          label: opt.label,
          prefix: <Icon aria-hidden="true" size={13} className="text-[var(--color-mute)]" />,
        };
      })}
      onChange={(next) => onChange(next as PerfFormFactor)}
      active={value !== 'all'}
    />
  );
}

// ─── Connection segmented control ───────────────────────────────────
//
// Sibling of the Audience pill. Friendly labels per the project's
// "non-technical audience" guideline — the raw `network_type` strings
// (4g / 3g / 2g / slow-2g / unknown) are bucketed into four legible
// choices the section's caller maps back to `network_types[]` on the
// way to the API.

const CONNECTION_OPTIONS: ReadonlyArray<{ value: PerfConnection; label: string }> = [
  { value: 'all', label: 'All connections' },
  { value: 'fast', label: 'Fast' },
  { value: 'slower', label: 'Slower' },
  { value: 'unknown', label: 'Unknown' },
];

function ConnectionPicker({
  value,
  onChange,
}: {
  value: PerfConnection;
  onChange: (next: PerfConnection) => void;
}): ReactElement {
  return (
    <FilterSelect
      ariaLabel="Connection"
      icon={<CellSignalFull aria-hidden="true" size={12} className="text-[var(--color-mute)]" />}
      value={value}
      options={CONNECTION_OPTIONS}
      onChange={(next) => onChange(next as PerfConnection)}
      active={value !== 'all'}
    />
  );
}

// ─── Country picker ─────────────────────────────────────────────────
//
// A `Select` populated with only the countries that actually have
// samples for this site in the current window. Country names come
// from `Intl.DisplayNames` (zero-dep, browser-native); flags are
// real SVGs from `country-flag-icons` via the shared `<Flag>`
// primitive so the picker, breakdown rows, and ranking card all
// use the same vocabulary.

const REGION_NAMES =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

function countryName(code: string): string {
  if (code === 'all') return 'All countries';
  return REGION_NAMES?.of(code) ?? code;
}

function CountryPicker({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (next: string) => void;
}): ReactElement {
  return (
    <FilterSelect
      ariaLabel="Country"
      // The selected country's flag doubles as the pill's glyph; the
      // globe stands in for "everywhere".
      icon={
        value === 'all' ? (
          <Globe aria-hidden="true" size={12} className="text-[var(--color-mute)]" />
        ) : (
          <Flag code={value} className="h-3 w-[18px]" />
        )
      }
      value={value}
      options={[
        { value: 'all', label: 'All countries' },
        ...options.map((code) => ({
          value: code,
          label: countryName(code),
          prefix: <Flag code={code} className="h-3 w-[18px]" />,
        })),
      ]}
      onChange={onChange}
      active={value !== 'all'}
    />
  );
}

// ─── Tile row ───────────────────────────────────────────────────────
//
// One grid, three rendering modes. The outer `<div key={state}>`
// remounts on state transitions so `animate-in fade-in duration-300`
// fires exactly once when data first lands (or when the queries
// resolve to an empty window). Within-state re-renders — e.g. a
// metric pill swap on the chart, or fresh data on a poll — keep the
// same key and don't re-fire the animation.

function PerformanceTileRow({
  perf,
  trend,
  formFactor,
  siteUuid,
  state,
  range,
  comparePerf,
  compareName,
  onMetricSelect,
}: {
  perf: PixelPerfRow[] | undefined;
  trend: PixelPerfTrendRow[] | undefined;
  formFactor: PerfFormFactor;
  siteUuid: string;
  state: SectionState;
  range: PageRange;
  /** Guest-site rows in compare mode — captions each tile with the
   *  guest's p75 for the same metric + audience. */
  comparePerf?: PixelPerfRow[] | undefined;
  compareName?: string | undefined;
  /** Makes each tile a click-through to that metric — the Overview's
   *  summary card passes it to deep-link into the Real users tab.
   *  Only honoured in the `data` state: an empty or still-loading tile
   *  has nothing to drill into, so it stays inert rather than offering
   *  a link to a blank view. */
  onMetricSelect?: ((metric: MetricKey) => void) | undefined;
}): ReactElement {
  const gridClass = 'grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]';
  const fadeOnArrival = state !== 'loading' ? 'animate-in fade-in duration-300' : '';

  if (state === 'loading') {
    // Plain bordered frames — same height as a real tile so the
    // surrounding layout doesn't shift when data lands. No skeleton
    // shimmer (it reads as "broken" at our density); no fake bars
    // (they'd morph into real bars and feel like a visual lie).
    return (
      <div key="loading" className={gridClass}>
        {METRICS.map((spec) => (
          <div
            key={spec.key}
            className="h-[140px] rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]"
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  if (state === 'empty') {
    // Full chrome: label, "—" hero, faint placeholder bars, neutral
    // caption. The route-level RumPlaceholderBanner does the "install
    // the pixel" instruction so the tiles stay quiet — saying it five
    // times here would be noise.
    return (
      <div key="empty" className={cn(gridClass, fadeOnArrival)}>
        {METRICS.map((spec) => (
          <PerformanceEmptyTile key={spec.key} label={spec.label} />
        ))}
      </div>
    );
  }

  // state === 'data'
  return (
    <div key="data" className={cn(gridClass, fadeOnArrival)}>
      {METRICS.map((spec) => {
        const resolved = resolveMetric(perf, formFactor, spec.key);
        const bars = trendBars(trend, formFactor, spec.key, range);
        const band = spec.band(resolved.p75);
        const caption = buildCaption(resolved);
        // Guest value in compare mode — same metric, same audience
        // filters, resolved off the guest rows. Renders as the tile's
        // compare footer; the hero row's delta pill carries the
        // relative verdict (home vs guest, direction-aware).
        const guest = compareName ? resolveMetric(comparePerf, formFactor, spec.key) : null;
        const guestDelta =
          guest && guest.samples > 0 && guest.p75 != null && guest.p75 > 0 && resolved.p75 != null
            ? ((resolved.p75 - guest.p75) / guest.p75) * 100
            : null;
        return (
          // Sample count + good-density live in the tile's hover tooltip
          // (title) rather than always-on caption text.
          <div key={spec.key} title={caption.title || undefined} className="space-y-1">
            <MetricTile<BarMeta>
              label={spec.label}
              value={spec.format(resolved.p75)}
              band={band}
              bars={bars}
              lowerIsBetter
              // Chip variant: neutral white value + brand-blue
              // spark bars (matching the Traffic page) + a small
              // band-coloured status chip in the top-right. Five
              // tiles in a row read as a status strip rather than
              // a wall of red/amber/green.
              variant="chip"
              barBand={(y) => spec.band(y)}
              barTitle={(d) =>
                d?.meta ? `${d.meta.day} · ${d.meta.formFactor} · ${spec.format(d.y)}` : ''
              }
              deltaPct={guestDelta}
              compare={
                guest && compareName
                  ? {
                      swatchColor: COMPARE_SERIES_COLOR,
                      label: compareName,
                      value: guest.samples > 0 ? spec.format(guest.p75) : '—',
                    }
                  : undefined
              }
              {...(onMetricSelect
                ? {
                    onClick: () => onMetricSelect(spec.key),
                    actionLabel: `Explore ${spec.label} in Real users`,
                  }
                : {})}
            />
            {/* Reserves the caption line (nbsp fallback) so the grid
                height matches the empty-state tiles; only speaks when
                there's no data. */}
            <p className="px-1 text-[10px] tabular-nums text-[var(--color-mute)]">
              {caption.text || ' '}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/** The sample count is useful but noisy as always-on text, so it (plus
 *  the good-density) lives in a hover tooltip. The visible caption stays
 *  empty for healthy tiles (the status chip already carries the signal)
 *  and only speaks up to say there's nothing in the window. */
function buildCaption(m: AggregatedMetric): { text: string; title: string } {
  // Always-empty visible text; the "—" hero already signals an empty
  // metric, and the detail (sample count / no-samples) lives in the
  // tile's hover tooltip.
  if (m.samples === 0) return { text: '', title: 'No samples in window' };
  const pct = m.good == null ? null : Math.round(m.good * 100);
  const sampleText = `${m.samples.toLocaleString()} sample${m.samples === 1 ? '' : 's'} in window`;
  return { text: '', title: pct == null ? sampleText : `${pct}% good · ${sampleText}` };
}

/**
 * Empty-state tile. Mirrors `MetricTile`'s outer chrome so the swap
 * to a real `MetricTile` on data arrival is geometrically identical —
 * same padding, same value baseline, same bar slot height. Renders
 * the label, a muted "—" hero, and the `PlaceholderBars` watermark.
 *
 * Caption text intentionally absent. Previously this tile carried a
 * "No samples yet" caption, which read as a confident claim even
 * when the parent query had errored (the tile can't distinguish
 * empty data from a failed fetch). The route-level
 * `RumPlaceholderBanner` already handles the "install the pixel"
 * messaging above the tiles; saying it five more times via captions
 * was redundant and misleading on transient failures.
 *
 * Now visually equivalent to passing `MetricTile` an empty `bars`
 * array — `MetricTile` falls back to the same `PlaceholderBars`
 * watermark when its `bars` are empty (e.g. the Audits-tab score
 * chips before any audits have run). Kept here for the CWV
 * `state === 'empty'` branch because the caller has no value /
 * band / delta to pass and the call site reads cleaner.
 */
function PerformanceEmptyTile({ label }: { label: string }): ReactElement {
  return (
    <div className="space-y-1">
      <div className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="truncate text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
            {label}
          </span>
        </div>
        <div className="mt-1.5 text-2xl font-semibold leading-none tabular-nums text-[var(--color-mute)]/70">
          —
        </div>
        <div className="mt-3">
          <PlaceholderBars />
        </div>
      </div>
    </div>
  );
}

// ─── Trend chart ────────────────────────────────────────────────────
//
// One metric (pill picker), one line per form factor when "All" is
// the audience — otherwise a single line. Mirrors `LiveTrendCurve`'s
// SVG style so the chart vocabulary stays consistent with the
// Traffic page.

const FORM_FACTOR_LINE_COLOR: Record<string, string> = {
  phone: 'var(--color-accent-brand)',
  tablet: '#8b7fd6',
  desktop: '#94a3b8',
  all: 'var(--color-accent-brand)',
};

function MetricPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded px-1.5 py-0.5 text-xs font-medium transition-colors',
        active
          ? 'font-semibold text-[var(--color-ink)]'
          : 'text-[var(--color-mute)] hover:text-[var(--color-ink)]',
      )}
    >
      {label}
    </button>
  );
}

/** The metric pill group. Lives in the chart header on the Audits tab
 *  (drives only that chart), or is promoted to a labelled section-level
 *  control on the real-user tab (drives the trend + ranking cards
 *  together, so it sits above all of them). */
function MetricSwitcher({
  metrics,
  metric,
  onMetricChange,
}: {
  metrics: readonly ChartMetricSpec[];
  metric: ChartMetricKey;
  onMetricChange: (m: ChartMetricKey) => void;
}): ReactElement {
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: pill group
      role="group"
      aria-label="Metric"
      className="flex flex-wrap items-center gap-0.5"
    >
      {metrics.map((m, i) => (
        <Fragment key={m.key}>
          {/* The Audits chart offers both planes' metrics in one group —
              four 0–100 category scores, then five vitals in ms/CLS.
              A hairline at the unit boundary keeps "SEO" and "LCP" from
              reading as neighbours in one flat list. No-op for the
              single-kind pill sets (Real users, per-page charts). */}
          {i > 0 && isLabSpec(metrics[i - 1]!) !== isLabSpec(m) && (
            <span aria-hidden="true" className="mx-1.5 h-4 w-px shrink-0 bg-[var(--color-line)]" />
          )}
          <MetricPill
            label={m.label}
            active={metric === m.key}
            onClick={() => onMetricChange(m.key)}
          />
        </Fragment>
      ))}
    </div>
  );
}

function PerformanceTrendChart({
  trend,
  trendCompare,
  formFactor,
  state,
  metric,
  onMetricChange,
  metrics,
  title,
  showPicker = true,
  source = 'rum',
  labPageUuid,
  labDeviceType,
  labRegion,
  range,
  siteUuid,
  compareTrend,
  compareLabTrend,
  compareName,
  homeName,
}: {
  trend: PixelPerfTrendRow[] | undefined;
  /** Same series over 2× the window — the older half is the previous
   *  period's comparison line. */
  trendCompare: PixelPerfTrendRow[] | undefined;
  formFactor: PerfFormFactor;
  state: SectionState;
  /** Metric pill selection — controlled at the section level so the
   *  Country Ranking card below can rank by the same metric. */
  metric: ChartMetricKey;
  onMetricChange: (m: ChartMetricKey) => void;
  /** Which metric pills to show — METRICS for the real-user tab,
   *  LAB_METRICS for the Lab tab. Keeps the two data planes in
   *  separate selectors. */
  metrics: readonly ChartMetricSpec[];
  /** Chart heading — "Web Vitals over time" vs "Audit scores over time". */
  title: string;
  /** Render the metric pills in the chart header. False on the
   *  real-user tab, where the switcher is promoted to a section-level
   *  control above the chart + ranking cards it drives. */
  showPicker?: boolean;
  /**
   * Which data plane the chart is reading. Can't be inferred from the
   * metric any more: a Lighthouse SCORE is always synthetic, but the
   * five vitals exist on both planes — the pixel measures them from
   * real visitors, and the audit trend carries its own `p75_*` from
   * Lighthouse runs. So the caller says which, and it decides three
   * things beyond the line itself: whose loading/empty state governs,
   * whether the previous-period overlay applies (RUM only), and whether
   * a point opens a session (a synthetic point is an audit run — there
   * is no session behind it).
   */
  source?: 'rum' | 'lab';
  /** Scope the synthetic (Audits) trend to one page. Undefined = origin
   *  (averaged across all audited pages). Only meaningful for Lab metrics. */
  labPageUuid?: string;
  /** Narrow the origin Lab trend by device / GCP region — the Origin
   *  sub-view's Device + Region filter. Undefined = unfiltered. Shares a
   *  query key with the chips' trend, so the two dedupe to one fetch. */
  labDeviceType?: 'mobile' | 'desktop' | undefined;
  labRegion?: string | undefined;
  range: PageRange;
  /** Drives the deploy-marker ticks along the chart baseline. */
  siteUuid: string;
  /** Guest-site comparison (page-level `?compare=`). `compareName` set
   *  = compare mode: the guest's series (RUM rows via `compareTrend`,
   *  Lab rows via `compareLabTrend`) overlays as a second line and the
   *  previous-period dashed overlay is suppressed — two reference
   *  lines against one curve was unreadable. */
  compareTrend?: PixelPerfTrendRow[] | undefined;
  compareLabTrend?: TrendBucketRow[] | undefined;
  compareName?: string | undefined;
  /** Active site's display name — labels the home line in compare mode
   *  (its usual key is a form factor, meaningless next to a site name). */
  homeName?: string | undefined;
}): ReactElement {
  const setMetric = onMetricChange;
  const spec = CHART_METRICS.find((m) => m.key === metric) ?? METRICS[0]!;
  // Synthetic either because the metric only exists there (a category
  // score) or because the caller is the Audits tab (a vital, read off
  // the audit trend's own p75 rather than the pixel's).
  const lab = isLabSpec(spec) || source === 'lab';
  // Deploy markers for the window — rendered as ticks on the baseline.
  // Degrades silently (empty array) if the endpoint 503s / is empty.
  const deploys = useSiteDeploys(siteUuid, range).data ?? [];
  // Synthetic (Lighthouse) trend — only consumed for Lab metrics, but the
  // hook runs unconditionally (cached, cheap) per the rules of hooks.
  const labTrend = useSiteTrend(siteUuid, {
    interval: 'day',
    range,
    ...(labPageUuid ? { page_uuid: labPageUuid } : {}),
    ...(labDeviceType ? { device_type: labDeviceType } : {}),
    ...(labRegion ? { region: labRegion } : {}),
  });

  // Lab metrics carry their own loading/empty state (from the synthetic
  // trend query), independent of the RUM `state` prop the caller passes.
  const effectiveState: SectionState = lab
    ? labTrend.isLoading
      ? 'loading'
      : labTrend.data && labTrend.data.length > 0
        ? 'data'
        : 'empty'
    : state;

  // One line: a Lab score from the synthetic trend, or the RUM CWV
  // line (averaged "all", or the selected device). Weekly-bucketed on
  // long ranges. Empty in loading/empty states.
  const homeLines =
    effectiveState !== 'data'
      ? []
      : lab
        ? buildLabSpecLine(labTrend.data, spec, range)
        : buildLines(trend, formFactor, metric as MetricKey, range);

  // Guest-site line in compare mode — same builder as the home line so
  // audience scoping + weekly bucketing match, rekeyed to the guest's
  // name and rendered as a faded dotted reference behind the home
  // line, which keeps its original band-coloured segments.
  const comparing = compareName != null;
  const guestLines =
    comparing && effectiveState === 'data'
      ? (lab
          ? buildLabSpecLine(compareLabTrend, spec, range)
          : buildLines(compareTrend, formFactor, metric as MetricKey, range)
        ).map((l) => ({ ...l, key: compareName, color: COMPARE_SERIES_COLOR, reference: true }))
      : [];
  // With two sites on the chart the home line's usual key (form factor
  // / metric) is meaningless — rekey it to the site name so the legend
  // + tooltip read "site vs site". Day-aligned because TrendCurve
  // positions points by index: without re-domaining, two sites with
  // different day coverage would drift out of time alignment.
  const baseLines =
    guestLines.length > 0
      ? alignLineDays([
          ...homeLines.map((l) => ({ ...l, key: homeName ?? 'This site' })),
          ...guestLines,
        ])
      : homeLines;

  // The sessions behind each point, for the day picker. Real-user
  // metrics only — a Lab point is a Lighthouse run, which the audit
  // trend already links to, and there's no session to open.
  const pickerMetric = !lab && effectiveState === 'data' ? (metric as MetricKey) : null;
  const worstSessions = useSiteWorstSessions(siteUuid, {
    metric: pickerMetric ?? 'lcp',
    days: range,
    ...(formFactor !== 'all' ? { formFactors: [formFactor] } : {}),
    enabled: pickerMetric != null,
  });
  // Re-derived from the source rows, not from `homeLines` — those are
  // already bucketed, and `shouldBucketWeekly` tests the pre-bucket
  // count. One trend row per (day, form_factor), so distinct days is
  // the count `buildLines` fed it under either audience selection.
  const weekly = shouldBucketWeekly(
    range,
    new Set(
      (trend ?? [])
        .filter((r) => formFactor === 'all' || r.form_factor === formFactor)
        .map((r) => r.day),
    ).size,
  );
  const lines = pickerMetric
    ? attachWorstSessions(baseLines, worstSessions.data, spec, weekly)
    : baseLines;

  // TrendCurve's click contract hands back only the item id, so the
  // rest of what the session route needs is looked up here: `t` bounds
  // the events scan, `path` gives the session view its back-link.
  const navigate = useNavigate();
  const sessionIndex = useMemo(() => {
    const index = new Map<string, { startedAt: string; path: string }>();
    for (const r of worstSessions.data ?? []) {
      index.set(r.session_uuid, { startedAt: r.started_at, path: r.page_path });
    }
    return index;
  }, [worstSessions.data]);
  const openSession = pickerMetric
    ? (sessionId: string): void => {
        const hit = sessionIndex.get(sessionId);
        if (!hit) return;
        void navigate({
          to: '/traffic/session/$sessionId',
          params: { sessionId },
          search: { t: hit.startedAt, path: hit.path },
        });
      }
    : null;

  // Previous-period comparison (RUM only for now): the older half of the
  // 2× window, run through the same builder so it buckets identically.
  // Rendered as a dashed muted overlay normalised across the same x-span.
  // Suppressed in compare mode — the guest line takes the overlay slot.
  const previousPoints =
    effectiveState === 'data' && !lab && !comparing && trendCompare?.length && trend?.length
      ? (() => {
          const earliest = trend.reduce((min, r) => (r.day < min ? r.day : min), trend[0]!.day);
          const prevRows = trendCompare.filter((r) => r.day < earliest);
          return buildLines(prevRows, formFactor, metric as MetricKey, range)[0]?.points ?? [];
        })()
      : [];

  // Body remounts on state transitions so the empty placeholder
  // fades cleanly into the real curve. `animate-in fade-in
  // duration-300` is gated on non-loading so the first paint doesn't
  // try to animate the loading frame.
  const fadeOnArrival = effectiveState !== 'loading' ? 'animate-in fade-in duration-300' : '';

  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-4 py-3">
        <div className="flex items-center gap-2">
          <ChartLine aria-hidden="true" size={16} className="text-[var(--color-mute)]" />
          <h3 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h3>
        </div>
        {showPicker && (
          <MetricSwitcher metrics={metrics} metric={metric} onMetricChange={setMetric} />
        )}
      </header>
      <div key={effectiveState} className={cn('px-4 py-5', fadeOnArrival)}>
        {effectiveState === 'loading' ? (
          // Plain chart-shaped frame matching the tile-row's loading
          // treatment — the whole page reads as one consistent
          // "UI is here, data on the way" state.
          <div className="h-[200px]" aria-hidden="true" />
        ) : lines.length === 0 ? (
          // Post-load empty: a calm inline message tied to the active
          // metric. Was previously the faint `PlaceholderCurve`, which
          // looked indistinguishable from the loading state.
          <div className="flex h-[200px] items-center justify-center text-xs text-[var(--color-mute)]">
            No {spec.label} samples in this window.
          </div>
        ) : (
          <TrendCurve
            lines={lines}
            spec={spec}
            deploys={deploys}
            previousPoints={previousPoints}
            {...(openSession
              ? { onPointClick: openSession, itemNoun: { one: 'session', many: 'sessions' } }
              : {})}
          />
        )}
      </div>
    </section>
  );
}

/**
 * Re-domain lines onto the union of their days so index i means the
 * same calendar day on every line — nulls hold their x slot in
 * TrendCurve, so this is what keeps a two-site overlay time-aligned
 * when the sites have different day coverage.
 */
function alignLineDays(lines: TrendLine[]): TrendLine[] {
  const days = [...new Set(lines.flatMap((l) => l.points.map((p) => p.day)))].sort();
  return lines.map((l) => {
    const byDay = new Map(l.points.map((p) => [p.day, p]));
    return { ...l, points: days.map((d) => byDay.get(d) ?? { day: d, value: null }) };
  });
}

function buildLines(
  trend: PixelPerfTrendRow[] | undefined,
  formFactor: PerfFormFactor,
  metric: MetricKey,
  rangeDays: number,
): TrendLine[] {
  if (!trend || trend.length === 0) return [];
  // "All devices" is a single averaged line (weighted daily blend across
  // form factors) — three overlapping lines were unreadable. A specific
  // device picks just that form factor. Either way it's one line, so the
  // segments band-colour cleanly.
  const rows =
    formFactor === 'all'
      ? aggregateTrendByDay(trend)
      : trend.filter((r) => r.form_factor === formFactor);
  let points = rows
    .slice()
    .sort((a, b) => a.day.localeCompare(b.day))
    .map((r) => ({ day: r.day, value: pickRow(r, metric).p75 }));
  if (shouldBucketWeekly(rangeDays, points.length)) points = bucketWeekly(points);
  return points.some((p) => p.value != null)
    ? [
        {
          key: formFactor === 'all' ? 'all' : formFactor,
          color: FORM_FACTOR_LINE_COLOR[formFactor] ?? 'var(--color-accent-brand)',
          points,
        },
      ]
    : [];
}

const WEEK_MS = 7 * 86_400_000;

/** Which plotted point a source day belongs to. Must match
 *  `bucketWeekly`'s grouping exactly, or items land on the wrong pip. */
function pointKeyForDay(day: string, weekly: boolean): string {
  if (!weekly) return day;
  const t = Date.parse(`${day}T00:00:00Z`);
  return Number.isNaN(t) ? day : String(Math.floor(t / WEEK_MS));
}

const FORM_FACTOR_SHORT: Record<string, string> = {
  phone: 'Phone',
  tablet: 'Tablet',
  desktop: 'Desktop',
};

/**
 * Hang the worst real sessions off each plotted point, so clicking a bad
 * day opens the picker and lands the reader in an actual session — the
 * same affordance the audit trend has.
 *
 * On weekly-bucketed ranges a point stands for a 7-day window, so
 * sessions are pooled across the whole bucket and re-ranked rather than
 * taken from the day that happens to label it: the picker has to offer
 * the worst sessions the point actually represents.
 *
 * Only the primary line gets items. In compare mode the reference line
 * is a different site, and these rows are the active site's.
 */
function attachWorstSessions(
  lines: TrendLine[],
  rows: PixelWorstSessionRow[] | undefined,
  spec: TrendCurveSpec,
  weekly: boolean,
  perPoint = 5,
): TrendLine[] {
  if (!rows || rows.length === 0) return lines;
  const byPoint = new Map<string, PixelWorstSessionRow[]>();
  for (const r of rows) {
    const key = pointKeyForDay(r.day, weekly);
    const bucket = byPoint.get(key);
    if (bucket) bucket.push(r);
    else byPoint.set(key, [r]);
  }

  let attached = false;
  return lines.map((line) => {
    if (line.reference || attached) return line;
    attached = true;
    return {
      ...line,
      points: line.points.map((p) => {
        const candidates = byPoint.get(pointKeyForDay(p.day, weekly));
        if (!candidates || candidates.length === 0) return p;
        const items = candidates
          .slice()
          .sort((a, b) => b.value - a.value)
          .slice(0, perPoint)
          .map((r) => ({
            id: r.session_uuid,
            label: [
              parseClickHouseUtc(r.started_at).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
              FORM_FACTOR_SHORT[r.form_factor] ?? r.form_factor,
              r.country_code,
            ]
              .filter(Boolean)
              .join(' · '),
            detail: spec.format(r.value),
          }));
        // A single item makes TrendCurve navigate straight through
        // instead of opening the picker, which is the right call — one
        // session to look at, no menu worth showing.
        return { ...p, items };
      }),
    };
  });
}

/** Build the single line for a Lab score from the synthetic trend. The
 *  trend has one row per (day, device-profile, region), so we average
 *  the score across those tuples per day. No form-factor dimension —
 *  Lighthouse runs are a fixed lab config. */
function buildLabLine(
  rows: TrendBucketRow[] | undefined,
  metric: LabScoreKey,
  rangeDays: number,
): TrendLine[] {
  if (!rows || rows.length === 0) return [];
  const field = LAB_FIELD[metric];
  const byDay = new Map<string, { sum: number; n: number }>();
  for (const r of rows) {
    const v = r[field];
    if (v == null) continue;
    const day = r.bucket.slice(0, 10);
    const cur = byDay.get(day) ?? { sum: 0, n: 0 };
    cur.sum += v;
    cur.n += 1;
    byDay.set(day, cur);
  }
  let points = [...byDay.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    // Lighthouse category scores are stored 0–1 in snapshots_events;
    // ×100 to the conventional 0–100 the axis + 90/50 thresholds expect.
    .map(([day, { sum, n }]) => ({ day, value: n > 0 ? (sum / n) * 100 : null }));
  if (shouldBucketWeekly(rangeDays, points.length)) points = bucketWeekly(points);
  return points.some((p) => p.value != null)
    ? [{ key: metric, color: 'var(--color-accent-brand)', points }]
    : [];
}

/** Per-day p75 series for one CWV from the synthetic trend — the CWV
 *  analogue of `buildLabLine`. Averages the metric's p75 across the
 *  (day, device-profile, region) tuples per day. No ×100: these are raw
 *  ms / CLS values, not 0–1 category scores. */
function buildLabCwvLine(
  rows: TrendBucketRow[] | undefined,
  field: 'p75_lcp' | 'p75_inp' | 'p75_cls' | 'p75_ttfb' | 'p75_fcp',
  rangeDays: number,
): Array<{ day: string; value: number | null }> {
  if (!rows || rows.length === 0) return [];
  const byDay = new Map<string, { sum: number; n: number }>();
  for (const r of rows) {
    const v = r[field];
    if (v == null) continue;
    const day = r.bucket.slice(0, 10);
    const cur = byDay.get(day) ?? { sum: 0, n: 0 };
    cur.sum += v;
    cur.n += 1;
    byDay.set(day, cur);
  }
  let points = [...byDay.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, { sum, n }]) => ({ day, value: n > 0 ? sum / n : null }));
  if (shouldBucketWeekly(rangeDays, points.length)) points = bucketWeekly(points);
  return points;
}

/** One line from the synthetic trend for whichever spec kind the Audits
 *  chart has selected — a category score, or a vital's p75. Both live on
 *  the same `TrendBucketRow`; only the column and the ×100 differ, which
 *  is why the two builders stay separate underneath. */
function buildLabSpecLine(
  rows: TrendBucketRow[] | undefined,
  spec: ChartMetricSpec,
  rangeDays: number,
): TrendLine[] {
  if (isLabSpec(spec)) return buildLabLine(rows, spec.key, rangeDays);
  const field = LAB_CWV_METRICS.find((m) => m.spec.key === spec.key)?.field;
  if (!field) return [];
  const points = buildLabCwvLine(rows, field, rangeDays);
  return points.some((p) => p.value != null)
    ? [{ key: spec.key, color: 'var(--color-accent-brand)', points }]
    : [];
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Below this sample count, a p75 is just one or two raw values wearing a
 * percentile's clothing — a single slow visitor can swing it to either
 * extreme. `samples > 0` let a 1-sample country/page rank identically to
 * one with hundreds, which is exactly backwards: low-n entries are MORE
 * likely to land at the extremes, so they clogged the top of "worst
 * first" instead of the real, high-volume signal.
 *
 * 10 matches `LOW_CONFIDENCE_FLOOR` in `apps/api/src/lib/audit-compare.ts`
 * — the existing bar this codebase already uses for "is this baseline
 * statistically trustworthy" (distinct from `next-actions`' traffic_floor
 * of 50, which asks the higher-bar question "is this worth recommending
 * optimisation work on").
 */
const MIN_SAMPLES_FOR_RANKING = 10;

// ─── Country ranking card ──────────────────────────────────────────
//
// Compact ranking of the countries with samples, sorted worst → best
// by the active metric (LCP / INP / CLS / TTFB / FCP). Each row shows
// the real flag (via `<Flag>`), country name (via Intl.DisplayNames),
// the p75 value tinted by its band, and the sample count.
//
// Why "worst first": the operator's question is almost always "where
// are we slow?", not "where are we fast?". The 5 worst countries are
// where regressions hide; the fastest countries are background.
//
// Hidden tile when the country payload contains no rows (the site has
// just one country's worth of data — ranking would be a single row).

function CountryRankingCard({
  rows,
  metric,
  loading,
}: {
  rows: PixelPerfByCountryRow[] | undefined;
  metric: MetricKey;
  loading: boolean;
}): ReactElement | null {
  const spec = METRICS.find((m) => m.key === metric)!;
  const pick = (r: PixelPerfByCountryRow): { p75: number | null; samples: number } => {
    switch (metric) {
      case 'lcp':
        return { p75: r.lcp_p75, samples: r.lcp_sample_count };
      case 'inp':
        return { p75: r.inp_p75, samples: r.inp_sample_count };
      case 'cls':
        return { p75: r.cls_p75, samples: r.cls_sample_count };
      case 'ttfb':
        return { p75: r.ttfb_p75, samples: r.ttfb_sample_count };
      case 'fcp':
        return { p75: r.fcp_p75, samples: r.fcp_sample_count };
    }
  };
  const ranked = (rows ?? [])
    .map((r) => ({ ...r, ...pick(r) }))
    .filter((r) => r.p75 != null && r.samples >= MIN_SAMPLES_FOR_RANKING)
    // Worst (highest p75) first — exception: CLS is also lower-is-
    // better; same direction. None of the surfaced CWV metrics are
    // higher-is-better, so a single sort direction works.
    .sort((a, b) => (b.p75 ?? 0) - (a.p75 ?? 0))
    .slice(0, 10);

  // Render the shell in three states — loading / empty / data — so
  // the panel stays in the layout regardless of what the upstream
  // pipe returns. The previous behaviour returned `null` when a site
  // had <2 ranked countries (or the fetch failed): the panel
  // vanished, the surrounding grid reflowed, and the user couldn't
  // tell whether the data was missing or the surface was broken.
  const isEmpty = !loading && ranked.length < 2;

  const maxP75 = Math.max(1, ...ranked.map((r) => r.p75 ?? 0));

  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-4 py-3">
        <div className="flex items-center gap-2">
          <Globe aria-hidden="true" size={16} className="text-[var(--color-mute)]" />
          <h3 className="text-sm font-semibold text-[var(--color-ink)]">{spec.label} by country</h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--color-mute)]">
          Worst first
        </span>
      </header>
      {isEmpty ? (
        <div className="flex h-[180px] items-center justify-center px-4 text-center text-xs text-[var(--color-mute)]">
          Not enough country data yet to rank.
        </div>
      ) : (
        <ul className="divide-y divide-[var(--color-line-soft)]/60">
          {loading && ranked.length === 0
            ? Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional skeleton rows
                <li key={i} className="h-9" aria-hidden="true" />
              ))
            : ranked.map((r) => {
                const band = spec.band(r.p75);
                const tone = bandText(band);
                const barPct = Math.max(8, Math.round(((r.p75 ?? 0) / maxP75) * 100));
                return (
                  <li
                    key={r.country_code}
                    // Sample count moves to a hover tooltip — same treatment
                    // as the metric tiles, so it isn't always-on noise.
                    title={`${r.samples.toLocaleString()} sample${r.samples === 1 ? '' : 's'}`}
                    className="relative h-9"
                  >
                    <div
                      aria-hidden="true"
                      className="absolute inset-y-0 left-0 bg-[var(--color-bar)]/8"
                      style={{ width: `${barPct}%` }}
                    />
                    <div className="relative grid h-full items-center px-3 [grid-template-columns:1fr_auto] gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Flag code={r.country_code} className="h-3 w-[18px]" />
                        <span className="truncate text-xs text-[var(--color-ink-soft)]">
                          {countryName(r.country_code)}
                        </span>
                      </div>
                      <span className={cn('text-xs font-semibold tabular-nums', tone)}>
                        {spec.format(r.p75)}
                      </span>
                    </div>
                  </li>
                );
              })}
        </ul>
      )}
    </section>
  );
}

// ─── Page ranking ──────────────────────────────────────────────────
//
// Sibling of CountryRankingCard but grouped by page_path. Two modes:
//   - 'worst'   → slowest pages first (rank by the active metric's p75)
//   - 'popular' → most-visited pages first (rank by pageviews), showing
//                 those pages' p75 — "where does our busiest traffic
//                 land on this metric?"
// Bar length tracks whatever the mode sorts by, so bars always descend.
// Same hidden-when-<2-rows rule as the country card.

function PageRankingCard({
  rows,
  metric,
  loading,
  mode,
}: {
  rows: PixelPerfByPageRow[] | undefined;
  metric: MetricKey;
  loading: boolean;
  mode: 'worst' | 'popular';
}): ReactElement | null {
  const spec = METRICS.find((m) => m.key === metric)!;
  const pick = (r: PixelPerfByPageRow): { p75: number | null; samples: number } => {
    switch (metric) {
      case 'lcp':
        return { p75: r.lcp_p75, samples: r.lcp_sample_count };
      case 'inp':
        return { p75: r.inp_p75, samples: r.inp_sample_count };
      case 'cls':
        return { p75: r.cls_p75, samples: r.cls_sample_count };
      case 'ttfb':
        return { p75: r.ttfb_p75, samples: r.ttfb_sample_count };
      case 'fcp':
        return { p75: r.fcp_p75, samples: r.fcp_sample_count };
    }
  };
  const ranked = (rows ?? [])
    .map((r) => ({ ...r, ...pick(r) }))
    .filter((r) => r.p75 != null && r.samples >= MIN_SAMPLES_FOR_RANKING)
    .sort((a, b) => (mode === 'popular' ? b.pageviews - a.pageviews : (b.p75 ?? 0) - (a.p75 ?? 0)))
    .slice(0, 10);

  // Same shell-stays-rendered rule as `CountryRankingCard`. The
  // panel always shows up; only the body content swaps among
  // loading skeleton rows / empty placeholder / ranked data.
  const isEmpty = !loading && ranked.length < 2;

  const maxBar =
    mode === 'popular'
      ? Math.max(1, ...ranked.map((r) => r.pageviews))
      : Math.max(1, ...ranked.map((r) => r.p75 ?? 0));
  const Icon = mode === 'popular' ? TrendUp : File;
  const title = mode === 'popular' ? `${spec.label} by popularity` : `${spec.label} by page`;
  const note = mode === 'popular' ? 'Most visited' : 'Worst first';
  const emptyCopy =
    mode === 'popular'
      ? 'Not enough page traffic yet to rank.'
      : 'Not enough page data yet to rank.';

  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon aria-hidden="true" size={16} className="text-[var(--color-mute)]" />
          <h3 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--color-mute)]">
          {note}
        </span>
      </header>
      {isEmpty ? (
        <div className="flex h-[180px] items-center justify-center px-4 text-center text-xs text-[var(--color-mute)]">
          {emptyCopy}
        </div>
      ) : (
        <ul className="divide-y divide-[var(--color-line-soft)]/60">
          {loading && ranked.length === 0
            ? Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional skeleton rows
                <li key={i} className="h-9" aria-hidden="true" />
              ))
            : ranked.map((r) => {
                const band = spec.band(r.p75);
                const tone = bandText(band);
                const barSource = mode === 'popular' ? r.pageviews : (r.p75 ?? 0);
                const barPct = Math.max(8, Math.round((barSource / maxBar) * 100));
                return (
                  <li
                    key={r.page_path}
                    // Sample + pageview counts move to a hover tooltip — same
                    // treatment as the metric tiles + country card.
                    title={`${r.samples.toLocaleString()} sample${r.samples === 1 ? '' : 's'} · ${r.pageviews.toLocaleString()} pageview${r.pageviews === 1 ? '' : 's'}`}
                    className="relative h-9"
                  >
                    <div
                      aria-hidden="true"
                      className="absolute inset-y-0 left-0 bg-[var(--color-bar)]/8"
                      style={{ width: `${barPct}%` }}
                    />
                    <div className="relative grid h-full items-center px-3 [grid-template-columns:1fr_auto] gap-3">
                      <span className="truncate text-xs text-[var(--color-ink-soft)]">
                        {r.page_path}
                      </span>
                      <span className={cn('text-xs font-semibold tabular-nums', tone)}>
                        {spec.format(r.p75)}
                      </span>
                    </div>
                  </li>
                );
              })}
        </ul>
      )}
    </section>
  );
}

// ─── Public component ──────────────────────────────────────────────

export function PerformanceSection({
  perf,
  trend,
  trendCompare,
  perfByCountry,
  perfByPage,
  loading,
  range,
  onRangeChange,
  formFactor,
  onFormFactorChange,
  country,
  onCountryChange,
  countryOptions,
  connection,
  onConnectionChange,
  metric,
  onMetricChange,
  siteUuid,
  comparePerf,
  compareTrend,
  compareName,
  homeName,
  actions,
}: PerformanceSectionProps): ReactElement {
  // Computed once at the section level so the tile row and the trend
  // chart agree on what's shown — no chance the two render different
  // states during a fetch.
  const state = deriveState(perf, loading);
  // RUM-only now — synthetic scores live on the dedicated Audits tab,
  // so this surface is unambiguously real-user. The metric also drives
  // the ranking cards below. Route-owned (see the prop doc); the
  // switcher + chart just call back into it.
  const setMetric = (next: ChartMetricKey): void => onMetricChange(next as MetricKey);
  const metricLabel = METRICS.find((m) => m.key === metric)?.label ?? 'Web Vitals';
  return (
    <div className="space-y-4">
      {/* Filter row. They scope everything below (tiles included), so
          they sit above the tiles rather than in the exploration band
          they used to head. Audience narrowing reads left-to-right from
          the left edge; the window + the route's Compare pill — the two
          controls that reframe the whole page rather than filter its
          population — anchor the right. Pinned like the Audits and Field
          data strips: which audience you're looking at has to stay
          answerable once the tiles have scrolled away. */}
      <FilterBar>
        <FormFactorPicker value={formFactor} onChange={onFormFactorChange} />
        <CountryPicker value={country} options={countryOptions} onChange={onCountryChange} />
        <ConnectionPicker value={connection} onChange={onConnectionChange} />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <PageRangeSelect value={range} onChange={onRangeChange} />
          {actions}
        </div>
      </FilterBar>

      {/* Overview band — the headline: all five CWV at p75, glanceable. */}
      <PerformanceTileRow
        perf={perf}
        trend={trend}
        formFactor={formFactor}
        siteUuid={siteUuid}
        state={state}
        range={range}
        comparePerf={comparePerf}
        compareName={compareName}
      />

      {/* Separation: summary above; the filtered exploration zone below.
          The metric switcher heads the band it drives. */}
      <div className="space-y-4 border-t border-[var(--color-line-soft)] pt-5">
        {/* Promoted metric switcher — drives the trend chart AND all three
            ranking cards, so it's a section-level control above them
            (not buried in the chart header) with an explicit label. Stays
            a pill row: unlike the page filters above it's a display knob
            for this band only, not an audience narrowing. */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
            Metric
          </span>
          <MetricSwitcher metrics={METRICS} metric={metric} onMetricChange={setMetric} />
        </div>

        <PerformanceTrendChart
          trend={trend}
          trendCompare={trendCompare}
          formFactor={formFactor}
          state={state}
          metric={metric}
          onMetricChange={setMetric}
          metrics={METRICS}
          title={`${metricLabel} over time`}
          showPicker={false}
          range={range}
          siteUuid={siteUuid}
          compareTrend={compareTrend}
          compareName={compareName}
          homeName={homeName}
        />

        {/* auto-fit so a sparse site (only the country card clears the
            ≥2-row bar) still fills the row instead of sitting at 1/3 width. */}
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
          <CountryRankingCard rows={perfByCountry} metric={metric} loading={state === 'loading'} />
          <PageRankingCard
            rows={perfByPage}
            metric={metric}
            loading={state === 'loading'}
            mode="worst"
          />
          <PageRankingCard
            rows={perfByPage}
            metric={metric}
            loading={state === 'loading'}
            mode="popular"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Lab (synthetic) summary + tab ─────────────────────────────────
//
// The Lab/Lighthouse scores are a different data plane from the
// real-user CWV above: synthetic runs against a device+region matrix,
// scored 0–100, scoped per URL (here averaged across all audited pages
// = "origin"). They live in their own tab + a teaser strip so the two
// planes never share a selector.

/** The four Lighthouse category scores as MetricTiles — hero score +
 *  band status chip + spark bars, matching the real-user CWV tiles so
 *  the two planes share one clean visual vocabulary. Backs the Audits
 *  tab headline. (The Overview's audit card shows CWV via `LabCwvChips`
 *  instead, so all three summary cards compare the same metrics.) */
function LabScoreChips({
  rows,
  range,
  compareRows,
  compareName,
}: {
  rows: TrendBucketRow[] | undefined;
  range: PageRange;
  /** Guest-site origin trend in compare mode — captions each chip with
   *  the guest's latest score. */
  compareRows?: TrendBucketRow[] | undefined;
  compareName?: string | undefined;
}): ReactElement {
  return (
    <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
      {LAB_METRICS.map((m) => {
        const points = buildLabLine(rows, m.key, range)[0]?.points ?? [];
        const latest = [...points].reverse().find((p) => p.value != null)?.value ?? null;
        const bars: CondensedBarsDatum<{ day: string }>[] = points.map((p) => ({
          x: Date.parse(`${p.day}T00:00:00Z`),
          y: p.value,
          meta: { day: p.day },
        }));
        // Guest's latest score in compare mode — rendered as the
        // tile's compare footer; the delta pill in the hero row
        // carries the relative verdict.
        const guestPoints = compareName
          ? (buildLabLine(compareRows, m.key, range)[0]?.points ?? [])
          : [];
        const guestLatest = [...guestPoints].reverse().find((p) => p.value != null)?.value ?? null;
        const guestDelta =
          latest != null && guestLatest != null && guestLatest > 0
            ? ((latest - guestLatest) / guestLatest) * 100
            : null;
        return (
          <MetricTile<{ day: string }>
            key={m.key}
            label={m.label}
            value={m.format(latest)}
            band={m.band(latest)}
            bars={bars}
            barBand={(y) => m.band(y)}
            // Scores are higher-is-better, unlike the CWV timings.
            lowerIsBetter={false}
            variant="chip"
            barTitle={(d) => (d?.meta ? `${d.meta.day} · ${m.format(d.y)}` : '')}
            deltaPct={guestDelta}
            compare={
              compareName
                ? {
                    swatchColor: COMPARE_SERIES_COLOR,
                    label: compareName,
                    value: m.format(guestLatest),
                  }
                : undefined
            }
          />
        );
      })}
    </div>
  );
}

/** The five synthetic Core Web Vitals as MetricTile chips — the same
 *  tiles the Real users + Field data summaries use, so all three planes
 *  read as one comparable row on the Overview. p75 across the audited
 *  device/region matrix; empty `—` shells when there's no audit data
 *  yet (matching the other two cards). FCP is newest to the trend pipe,
 *  so its tile stays an empty shell until the pipe redeploys. */
function LabCwvChips({
  rows,
  range,
  onMetricSelect,
}: {
  rows: TrendBucketRow[] | undefined;
  range: PageRange;
  /** Makes each chip a click-through to that vital on the Audits tab
   *  (the Overview's audit card). Inert on a chip with no runs behind
   *  it — the chart would have nothing to draw. */
  onMetricSelect?: ((metric: MetricKey) => void) | undefined;
}): ReactElement {
  return (
    <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
      {LAB_CWV_METRICS.map(({ spec, field }) => {
        const points = buildLabCwvLine(rows, field, range);
        const latest = [...points].reverse().find((p) => p.value != null)?.value ?? null;
        const bars: CondensedBarsDatum<{ day: string }>[] = points.map((p) => ({
          x: Date.parse(`${p.day}T00:00:00Z`),
          y: p.value,
          meta: { day: p.day },
        }));
        return (
          <MetricTile<{ day: string }>
            key={spec.key}
            label={spec.label}
            value={spec.format(latest)}
            band={spec.band(latest)}
            bars={bars}
            lowerIsBetter
            variant="chip"
            barBand={(y) => spec.band(y)}
            barTitle={(d) => (d?.meta ? `${d.meta.day} · ${spec.format(d.y)}` : '')}
            {...(onMetricSelect && latest != null
              ? {
                  onClick: () => onMetricSelect(spec.key),
                  actionLabel: `Explore ${spec.label} in Audits`,
                }
              : {})}
          />
        );
      })}
    </div>
  );
}

// Plain-language explainers behind each plane's badge tooltip on the
// Overview — what the source measures + its scope, so the "Worldwide" vs
// "Your regions" labels have context on hover. The scope contrast is the
// point: pixel + CrUX are global, audits only cover the regions the user
// configured.
export const OVERVIEW_EXPLAINER =
  'Overview — a side-by-side summary of all three sources: your pixel (real users), Google field data (CrUX), and synthetic Lighthouse audits.';
export const RUM_EXPLAINER =
  'Real users — Core Web Vitals your actual visitors experienced, captured by the Clickweave pixel. Every visitor worldwide, at the 75th percentile (p75).';
export const CRUX_EXPLAINER =
  "Field data — Google's Chrome UX Report: real Chrome users worldwide over a rolling 28-day window (p75). This is what Google Search sees for your origin.";
export const AUDIT_LOCATION_CAVEAT =
  'Audits — synthetic Lighthouse lab tests Clickweave runs only from the regions and devices you configured (not worldwide like the pixel + CrUX cards), at p75 across those runs.';

/** The Overview's audit summary card: the five synthetic CWV as tile
 *  chips (matching the RUM + CrUX cards) with a deep-link into the
 *  Audits tab. Always renders the tile shell — empty `—` tiles when no
 *  audits have run — so the card never vanishes; the header link is the
 *  path to set audits up, mirroring the other cards' onboarding. A
 *  badge + tooltip flag that, unlike the global pixel/CrUX data, these
 *  vitals are scoped to the user's configured audit locations. */
function LabSummaryStrip({
  siteUuid,
  range,
  onMetricSelect,
}: {
  siteUuid: string;
  range: PageRange;
  onMetricSelect: (metric: MetricKey) => void;
}): ReactElement {
  const labTrend = useSiteTrend(siteUuid, { interval: 'day', range });
  // No audits scheduled yet — cover the shells with a setup link, same
  // pattern as the pixel card (audits are user-actionable, so it links).
  const auditsCovered = !labTrend.isLoading && (labTrend.data ?? []).length === 0;
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-4 py-3">
        <div className="flex items-center gap-2">
          <ChartBar aria-hidden="true" size={16} className="text-[var(--color-mute)]" />
          <h3 className="text-sm font-semibold text-[var(--color-ink)]">Audits</h3>
          <PlaneBadge label="Lab · your regions" tooltip={AUDIT_LOCATION_CAVEAT} />
        </div>
        <Link
          to="/performance/audits"
          className="inline-flex items-center gap-1 text-xs text-[var(--color-ink-soft)] transition-colors hover:text-[var(--color-ink)]"
        >
          Open Audits
          <ArrowRight aria-hidden="true" size={13} />
        </Link>
      </header>
      <div className="px-4 py-3">
        <PlaneCover covered={auditsCovered} href="/settings/sites" label="Set up audits">
          <LabCwvChips rows={labTrend.data} range={range} onMetricSelect={onMetricSelect} />
        </PlaneCover>
      </div>
    </section>
  );
}

/**
 * Audits view — the synthetic (Lighthouse) plane. One layout at both
 * zoom levels: category-score chips, CWV chips, the focus trend chart
 * for the selected metric, then a chart per metric grouped as Scores ·
 * Web Vitals · Page weight · Third parties (`AuditMetricCharts`).
 *
 * The scope picker only narrows what feeds them — Origin (every audited
 * page) or one page. It used to be an Origin / Pages sub-tab pair, where
 * each half had what the other lacked: the summary was origin-only and
 * the per-metric charts were page-only. Same question, two zoom levels,
 * so it became a dropdown in the filter bar and both halves now answer
 * at either zoom. Individual reports live in ./$auditId.
 */
export function LabPerformance({
  siteUuid,
  range,
  onRangeChange,
  metric,
  onMetricChange,
  compareSiteUuid,
  compareName,
  homeName,
  pageUuid,
  onPageChange,
  actions,
}: {
  siteUuid: string;
  range: PageRange;
  onRangeChange: (r: PageRange) => void;
  /** Selected metric for the origin chart — a Lighthouse category score
   *  or one of the five vitals. Route-owned (`?metric=`), like the other
   *  two planes', so the Overview's audit tiles can deep-link into a
   *  vital and the choice is shareable. */
  metric: ChartMetricKey;
  onMetricChange: (next: ChartMetricKey) => void;
  /** Guest-site comparison (page-level `?compare=`). The score chips +
   *  scores-over-time chart compare at origin level; the per-page
   *  breakdown pairs the home page with a user-picked guest page (pages
   *  don't correspond across sites, so the pairing is explicit — a
   *  "vs page" picker, defaulting to the guest page with the matching
   *  URL path). Null/undefined when comparison is off. */
  compareSiteUuid?: string | null;
  compareName?: string | undefined;
  homeName?: string | undefined;
  /** Scope: a page uuid, or `AUDIT_SCOPE_ORIGIN` for the whole origin.
   *  Route-owned (`?page=`) so a page view is shareable — the same deep
   *  link the removed `/pages/:pageUuid` detail route left behind. */
  pageUuid: string;
  onPageChange: (next: string) => void;
  /** Trailing slot in the sticky filter bar — the route's Compare
   *  picker, sat beside the date range. */
  actions?: ReactNode;
}): ReactElement {
  const metricLabel = AUDIT_CHART_METRICS.find((m) => m.key === metric)?.label ?? 'Audit scores';
  // Null = the origin summary; a uuid = that page's audit history.
  const scopedPageUuid = pageUuid === AUDIT_SCOPE_ORIGIN ? null : pageUuid;
  // Origin filter — Device + Region narrow the origin summary (all pages
  // averaged) the same way the per-page view narrows one page. useSiteTrend
  // takes device_type ('mobile'|'desktop') + region (the GCP slug), so both
  // filter server-side; 'all' just omits the param. Passed on to the
  // scores-over-time chart too, so chips + chart stay in lockstep.
  const [device, setDevice] = useState<DeviceFilter>('all');
  const [region, setRegion] = useState<string>('all');
  const labDeviceType = device !== 'all' ? device : undefined;
  const labRegion = region !== 'all' ? region : undefined;
  const trendFilter = useMemo(
    () => ({
      interval: 'day' as const,
      range,
      ...(scopedPageUuid ? { page_uuid: scopedPageUuid } : {}),
      ...(labDeviceType ? { device_type: labDeviceType } : {}),
      ...(labRegion ? { region: labRegion } : {}),
    }),
    [range, scopedPageUuid, labDeviceType, labRegion],
  );
  // Audited pages drive the Pages section's picker. The `metrics` arg
  // only controls the recent_audits payload we don't use here — we just
  // need the page list (uuid + name).
  const pagesQuery = useSitePagesWithAudits(siteUuid, ['performance']);
  // The API returns pages newest-created first, which made the picker
  // default (`pages[0]`) whatever page was added most recently. Creation
  // order puts the site's first page — usually the homepage — first and
  // selected by default.
  const pages = useMemo(
    () =>
      [...(pagesQuery.data ?? [])].sort(
        (a, b) => Date.parse(a.created_at) - Date.parse(b.created_at),
      ),
    [pagesQuery.data],
  );
  // The origin summary is origin-level (all pages), narrowed by the
  // Device/Region filter. The chips + chart read this same filtered trend.
  const labTrend = useSiteTrend(siteUuid, trendFilter);
  // Schedule gate — a site with no audit schedule and no audit history
  // gets the CrUX-style cover (dimmed frame + explanation card) pointing
  // at Settings → Audits, where the schedule now lives. Sites with
  // ad-hoc history keep their data uncovered even without a schedule.
  const site = useSite(siteUuid);
  const needsSchedule =
    !!site.data &&
    site.data.audit.schedule === null &&
    !labTrend.isLoading &&
    (labTrend.data ?? []).length === 0;
  // Guest trend in compare mode — the hook no-ops on null. Built from
  // the same filters minus the page scope, which is re-pointed at the
  // guest's own page below (a home page uuid means nothing to it).
  const guestTrendFilter = useMemo(() => {
    const { page_uuid: _home, ...rest } = trendFilter;
    return rest;
  }, [trendFilter]);
  // Region options: the distinct GCP regions that actually have audits
  // (a recent sample covers the audit grid). Narrowed to the scoped page
  // when there is one — offering a region that page is never audited from
  // would only ever produce an empty chart. `include_ids` is what makes
  // that per-page narrowing possible off this one site-wide sample.
  const originCreatedAfter = useMemo(
    () => new Date(Date.now() - range * 86_400_000).toISOString(),
    [range],
  );
  const originAudits = useAudits({
    limit: 200,
    include_ids: true,
    site_uuid: siteUuid,
    created_after: originCreatedAfter,
  });
  const regionOptions = useMemo<ReadonlyArray<{ value: string; label: ReactNode }>>(() => {
    const sample = (originAudits.data?.audits ?? []).filter(
      (a) => !scopedPageUuid || a.ids?.page_uuid === scopedPageUuid,
    );
    const distinct = [...new Set(sample.map((a) => a.region).filter(Boolean))].sort();
    return [
      { value: 'all', label: 'All regions' },
      ...distinct.map((r) => ({ value: r, label: regionLabel(r) })),
    ];
  }, [originAudits.data, scopedPageUuid]);
  const comparing = !!compareSiteUuid && compareName != null;
  // Guest site's audited pages — feed the "vs page" picker. Same sort as the home list so both pickers lead with the
  // site's first (usually home) page.
  const guestPagesQuery = useSitePagesWithAudits(comparing ? compareSiteUuid : null, [
    'performance',
  ]);
  const guestPages = useMemo(
    () =>
      [...(guestPagesQuery.data ?? [])].sort(
        (a, b) => Date.parse(a.created_at) - Date.parse(b.created_at),
      ),
    [guestPagesQuery.data],
  );
  // Guest page in compare mode, defaulting to the guest page whose URL
  // path matches the scoped page (homepage → homepage), else the guest's
  // first. Null = "no explicit pick yet", so changing the scoped page
  // re-derives the match until the user pins a guest page themselves.
  const [guestPageUuid, setGuestPageUuid] = useState<string | null>(null);
  const homePage = pages.find((p) => p.uuid === scopedPageUuid);
  // Same-site (page-vs-page): the guest list IS the home list, so a
  // path-match would default guest to the SAME page (a pointless
  // page-vs-itself overlay). Default to the first DIFFERENT page instead.
  const sameSite = comparing && compareSiteUuid === siteUuid;
  const guestSelected =
    guestPageUuid && guestPages.some((p) => p.uuid === guestPageUuid)
      ? guestPageUuid
      : sameSite
        ? (guestPages.find((g) => g.uuid !== scopedPageUuid)?.uuid ?? guestPages[0]?.uuid)
        : (guestPages.find((g) => homePage && pagePath(g) === pagePath(homePage))?.uuid ??
          guestPages[0]?.uuid);
  // At origin the comparison is site-vs-site, so a same-site pick has
  // nothing to say (an origin against itself); scoped to a page it's the
  // page-vs-page affordance and needs a guest page to pair with.
  const compareScope = scopedPageUuid ? (guestSelected ?? null) : null;
  const compareActive =
    comparing && (scopedPageUuid ? !!guestSelected : compareSiteUuid !== siteUuid);
  const guestLabTrend = useSiteTrend(compareActive ? (compareSiteUuid ?? null) : null, {
    ...guestTrendFilter,
    ...(compareScope ? { page_uuid: compareScope } : {}),
  });
  const compareRows = compareActive ? guestLabTrend.data : undefined;
  const compareLabel = compareActive ? compareName : undefined;
  const pageCompare =
    compareActive && compareSiteUuid && compareName
      ? {
          siteUuid: compareSiteUuid,
          siteName: compareName,
          homeName,
          pages: guestPages,
          pageUuid: compareScope,
        }
      : undefined;
  return (
    <div className="relative">
      <div
        className={cn('space-y-4', needsSchedule && 'pointer-events-none select-none opacity-40')}
        aria-hidden={needsSchedule || undefined}
      >
        {/* One filter bar for both zoom levels: scope (Origin vs a page) +
          the guest-page picker in compare mode, then Device · Region ·
          Range · Compare. */}
        <AuditFilterBar
          device={device}
          onDevice={setDevice}
          region={region}
          onRegion={setRegion}
          regionOptions={regionOptions}
          range={range}
          onRange={onRangeChange}
          actions={actions}
        >
          <AuditScopeSelect value={pageUuid} pages={pages} onChange={onPageChange} />
          {/* Only page scope pairs page-to-page; at origin the comparison
            is whole-site and there is nothing to pick. */}
          {pageCompare?.pageUuid && (
            <PageSelect
              label={sameSite ? 'Compare page' : `vs ${pageCompare.siteName}`}
              value={pageCompare.pageUuid}
              pages={guestPages}
              onChange={setGuestPageUuid}
            />
          )}
        </AuditFilterBar>
        <div className="space-y-3">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
            Lighthouse scores
          </h3>
          <LabScoreChips
            rows={labTrend.data}
            range={range}
            compareRows={compareRows}
            compareName={compareLabel}
          />
        </div>
        <div className="space-y-3">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
            Core Web Vitals
          </h3>
          <LabCwvChips rows={labTrend.data} range={range} />
        </div>
        <PerformanceTrendChart
          trend={undefined}
          trendCompare={undefined}
          formFactor="all"
          state="data"
          metric={metric}
          onMetricChange={onMetricChange}
          metrics={AUDIT_CHART_METRICS}
          title={`${metricLabel} over time`}
          source="lab"
          labPageUuid={scopedPageUuid ?? undefined}
          labDeviceType={labDeviceType}
          labRegion={labRegion}
          range={range}
          siteUuid={siteUuid}
          compareLabTrend={compareRows}
          compareName={compareLabel}
          homeName={homeName}
        />
        {/* Chart per metric, at whichever zoom the scope is set to.
              Origin folds every audited page into the same per-day mean
              the per-page view already applies across runs. */}
        <AuditMetricCharts
          siteUuid={siteUuid}
          pageUuid={scopedPageUuid}
          pages={pages}
          range={range}
          device={device}
          region={region}
          compare={pageCompare}
        />
      </div>
      {/* Mirrors CruxEmptyReport's no-coverage presentation: the page's
          own empty shells dimmed behind an explanation card — except
          audits ARE user-actionable, so the card carries the setup
          link. Anchored near the top (not centered): this wrapper spans
          the whole tall page, so a vertical center would put the card
          below the fold. */}
      {needsSchedule && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-start justify-center p-4 pt-[22vh]">
          <div className="pointer-events-auto flex max-w-sm flex-col items-center gap-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] px-6 py-5 text-center shadow-md">
            <CalendarCheck
              aria-hidden="true"
              size={28}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <p className="text-xs text-[var(--color-ink-soft)]">
              Audits aren't scheduled for this site yet. Pick where to test from, which devices to
              emulate, and how often to run.
            </p>
            <Link
              to="/settings/sites"
              className="inline-flex items-center gap-1 rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-2.5 py-1.5 text-xs font-medium text-[var(--color-ink)] shadow-sm transition-colors hover:bg-[var(--color-paper)]"
            >
              Set up a schedule
              <ArrowRight aria-hidden="true" size={13} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/** URL path of a page (fallback: the raw url) — the cross-site pairing
 *  key for the guest-page default. */
function pagePath(page: PageWithRecentAudits): string {
  try {
    return new URL(page.url).pathname || page.url;
  } catch {
    return page.url;
  }
}

// ─── Origin overview ────────────────────────────────────────────────
//
// The `/performance` landing tab: a glanceable, cross-plane summary of
// the site's real-user (pixel), field (CrUX), and synthetic (audit)
// performance — each in its own card that deep-links into the full tab.
// Deliberately control-free (no audience filters or range pill): it's
// the "everything at once" summary; the per-plane tabs own the
// exploration. Every card carries the same loading / empty states as
// its source tab, so the overview never invents data. A site with no
// pixel data shows the empty CWV tile shells (label + "—" + placeholder
// bars) — a preview of what the data will fill in — rather than an
// install banner. The connect CTA lives on the Real users tab, reached
// via the card's "Open Real users" deep-link.

/** A plane badge ("source · scope") that doubles as a tooltip trigger —
 *  hover (or focus) for a plain-language explainer of what the plane
 *  measures. Shared by all three Overview summary cards so the badges +
 *  their tooltips read consistently. Uses the site's `ui/tooltip`, not a
 *  native `title`.
 *
 *  Exported for the Segments surfaces, which are pixel-RUM-only and
 *  carry the same "Real users" badge + `RUM_EXPLAINER` so a reader who
 *  learned what the badge means on the Overview reads it the same way
 *  there. */
export function PlaneBadge({ label, tooltip }: { label: string; tooltip: string }): ReactElement {
  return (
    // `delay={0}` — open on hover instantly. base-ui's ~600ms default
    // made the badge feel un-interactive (you had to hover + wait); the
    // delay lives on the Provider in this base-ui build, not the Root.
    <TooltipProvider delay={0}>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              className="inline-flex cursor-help items-center gap-1 rounded-full border border-[var(--color-line)] px-1.5 py-px text-[10px] uppercase tracking-[0.04em] text-[var(--color-mute)] outline-none transition-colors hover:text-[var(--color-ink-soft)] focus-visible:text-[var(--color-ink-soft)]"
            >
              {label}
              <Info aria-hidden="true" size={11} />
            </button>
          }
        />
        <TooltipPopup side="bottom" sideOffset={6} className="max-w-xs normal-case">
          {tooltip}
        </TooltipPopup>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Shared card chrome for the RUM + CrUX summary cards — mirrors the
 *  `LabSummaryStrip` section so all three planes read as one row. */
function PlaneSummaryCard({
  icon,
  title,
  badge,
  badgeTooltip,
  view,
  linkLabel,
  children,
}: {
  icon: ReactElement;
  title: string;
  badge: string;
  badgeTooltip: string;
  view: 'real' | 'field';
  linkLabel: string;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-4 py-3">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h3>
          <PlaneBadge label={badge} tooltip={badgeTooltip} />
        </div>
        <Link
          to={view === 'real' ? '/performance/real' : '/performance/field'}
          className="inline-flex items-center gap-1 text-xs text-[var(--color-ink-soft)] transition-colors hover:text-[var(--color-ink)]"
        >
          {linkLabel}
          <ArrowRight aria-hidden="true" size={13} />
        </Link>
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

/** A subtle gradient scrim over an empty tile row. The empty "—" shells
 *  stay visible behind the veil (still "here's what will fill in"), with
 *  a centred nudge on top: a setup link for the planes the user can act
 *  on (pixel, audits), or a one-line reason for CrUX, whose coverage
 *  Google controls. Built from palette tokens — the veil is the card's
 *  own `--color-paper` fading up, the nudge a `--color-paper-alt` pill —
 *  so it sits in the theme (light + dark) rather than a raw black scrim.
 *  `covered=false` renders children untouched. */
function PlaneCover({
  covered,
  href,
  label,
  children,
}: {
  covered: boolean;
  /** Renders `label` as a setup link. Omit for a plain reason line. */
  href?: string;
  label: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="relative">
      <div className={cn(covered && 'pointer-events-none select-none')}>{children}</div>
      {covered && (
        <div className="absolute inset-0 flex items-center justify-center rounded-[inherit] bg-gradient-to-t from-[var(--color-paper)]/95 via-[var(--color-paper)]/75 to-[var(--color-paper)]/40 px-4 text-center backdrop-blur-[1px]">
          {href ? (
            <a
              href={href}
              className="pointer-events-auto inline-flex items-center gap-1 rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-2.5 py-1.5 text-xs font-medium text-[var(--color-ink)] shadow-sm transition-colors hover:bg-[var(--color-paper)]"
            >
              {label}
              <ArrowRight aria-hidden="true" size={13} />
            </a>
          ) : (
            <span className="rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-2.5 py-1.5 text-[11px] font-medium text-[var(--color-ink-soft)] shadow-sm">
              {label}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** The origin overview — the default `/performance` tab. Three
 *  cross-plane summary cards (real-user pixel, CrUX field data,
 *  synthetic audits), each deep-linking into its full tab: the header
 *  link opens the tab, and an individual tile opens it with that vital
 *  already selected in the tab's metric filter — so "LCP looks bad
 *  here" goes straight to the LCP trend + rankings instead of landing
 *  on the tab's LCP-by-default view and re-picking. */
export function OverviewSection({
  siteUuid,
  range,
  perf,
  trend,
  perfLoading,
  perfSettled,
  rumConnected,
  crux,
  onOpenMetric,
}: {
  siteUuid: string;
  range: PageRange;
  /** Real-user headline series — all-audience p75s + daily sparkline.
   *  Undefined while loading. */
  perf: PixelPerfRow[] | undefined;
  trend: PixelPerfTrendRow[] | undefined;
  /** Pixel query initial-load gate (isLoading, not isFetching). */
  perfLoading: boolean;
  /** Pixel query resolved at least once — gates the setup cover so it
   *  can't flash on an installed site during cold load. */
  perfSettled: boolean;
  /** Health probe says the site has ever shipped a beacon. */
  rumConnected: boolean;
  /** Origin CrUX ALL-series (already fetched for the Field tab +
   *  landing). Undefined → the field card renders empty tile shells. */
  crux: CruxResponse | undefined;
  /** Click-through for a summary tile: open that plane's full tab with
   *  the clicked vital already selected in its metric filter. */
  onOpenMetric: (view: 'real' | 'field' | 'audits', metric: MetricKey) => void;
}): ReactElement {
  // Pixel setup gate — cover the shells only once we're sure there's no
  // pixel: settled, never beaconed, and no samples in the window.
  const anyPerfSamples = (perf ?? []).some((r) =>
    [
      r.lcp_sample_count,
      r.inp_sample_count,
      r.cls_sample_count,
      r.ttfb_sample_count,
      r.fcp_sample_count,
    ].some((n) => n > 0),
  );
  const pixelCovered = perfSettled && !rumConnected && !anyPerfSamples;

  // CrUX coverage — cover with a one-line reason once we have a
  // definitive not-covered answer. Google's traffic threshold isn't
  // something the user can act on, so it's a reason, not a setup link.
  const cruxCovered = !!crux && (crux.coverage !== 'ok' || crux.series.length === 0);
  const cruxReason =
    crux?.coverage === 'no_data'
      ? 'Not enough Chrome traffic for field data yet'
      : 'Field data loading — check back soon';

  return (
    <div className="space-y-4">
      {/* Real users (pixel RUM) — the five CWV p75 tiles. A site with no
          pixel data shows the empty "—" shells behind a subtle gradient
          cover + a "Set up the pixel" link (the shells preview what will
          fill in). Installed sites render the tiles uncovered. */}
      <PlaneSummaryCard
        icon={<Pulse aria-hidden="true" size={16} className="text-[var(--color-mute)]" />}
        title="Real users"
        badge="Pixel · worldwide"
        badgeTooltip={RUM_EXPLAINER}
        view="real"
        linkLabel="Open Real users"
      >
        <PlaneCover covered={pixelCovered} href="/docs/pixel" label="Set up the pixel">
          <PerformanceTileRow
            perf={perf}
            trend={trend}
            formFactor="all"
            state={deriveState(perf, perfLoading)}
            range={range}
            siteUuid={siteUuid}
            onMetricSelect={(m) => onOpenMetric('real', m)}
          />
        </PlaneCover>
      </PlaneSummaryCard>

      {/* Field data (Chrome UX Report) — the same five CWV tile chips
          the full report leads with, matching the RUM + audit summaries.
          No coverage → the empty shells behind the same gradient cover
          with a one-line reason (not a link — CrUX coverage is Google's
          call). The header deep-links to the full Field data report. */}
      <PlaneSummaryCard
        icon={
          <GoogleChromeLogo
            aria-hidden="true"
            size={16}
            weight="light"
            className="text-[var(--color-mute)]"
          />
        }
        title="Field data"
        badge="CrUX · worldwide"
        badgeTooltip={CRUX_EXPLAINER}
        view="field"
        linkLabel="Open Field data"
      >
        <PlaneCover covered={cruxCovered} label={cruxReason}>
          <CruxSection
            data={crux}
            variant="summary"
            onMetricSelect={(m) => onOpenMetric('field', m)}
          />
        </PlaneCover>
      </PlaneSummaryCard>

      {/* Synthetic audit scores — moved here from the real-user tab.
          Self-contained: own header, "Open Audits" deep-link, and
          "no audits run yet" empty state. */}
      <LabSummaryStrip
        siteUuid={siteUuid}
        range={range}
        onMetricSelect={(m) => onOpenMetric('audits', m)}
      />
    </div>
  );
}
